import React from 'react';
import { useTranslation } from 'react-i18next';
import ModalShell from './ModalShell';

const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText, type = 'danger' }) => {
  const { t } = useTranslation('common');
  if (!isOpen) return null;
  const confirmLabel = confirmText || t('confirmDialog.confirm');
  const cancelLabel = cancelText || t('confirmDialog.cancel');
  const titleText = title || t('confirmDialog.defaultTitle');

  const typeStyles = {
    danger: 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700',
    warning: 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700',
    info: 'bg-gradient-to-r from-[#2f6fd4] to-[#113a5b] hover:from-[#2563c4] hover:to-[#0f3350]',
    success: 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700',
  };

  const iconStyles = {
    danger: 'bg-red-50 text-red-600 ring-1 ring-red-100',
    warning: 'bg-amber-50 text-amber-700 ring-1 ring-amber-100',
    info: 'bg-[#edf4ff] text-[#2f6fd4] ring-1 ring-[#bcd4f6]',
    success: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
  };

  const getIcon = () => {
    if (type === 'danger') {
      return (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
        </svg>
      );
    }
    if (type === 'warning') {
      return (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
        </svg>
      );
    }
    if (type === 'info') {
      return (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
        </svg>
      );
    }
    if (type === 'success') {
      return (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
        </svg>
      );
    }
    return null;
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <ModalShell
      maxWidthClass="max-w-md"
      zClass="z-[150]"
      panelClassName="overflow-hidden p-0"
      onBackdropClick={onClose}
    >
      <div className="mm-modal-header !border-b-0 !bg-transparent">
        <div className="flex min-w-0 flex-1 items-start gap-3.5">
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${iconStyles[type]}`}>
            {getIcon()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="mm-hero__label text-slate-400">{t('confirmDialog.kicker', { defaultValue: 'Confirmation' })}</p>
            <h3 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350] sm:text-xl">
              {titleText}
            </h3>
            <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-slate-600 sm:text-[15px]">
              {message}
            </p>
          </div>
        </div>
      </div>
      <div className="mm-modal-footer">
        <button type="button" onClick={onClose} className="mm-cta mm-cta--ghost flex-1">
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          className={`mm-cta flex-1 border-0 shadow-none ${typeStyles[type]}`}
        >
          {confirmLabel}
        </button>
      </div>
    </ModalShell>
  );
};

export default ConfirmModal;
